import * as z from "zod";
import type { TFunction } from "i18next";

import { requiredApiDecimalString } from "@/shared/utils";

import type { StructureType } from "../api/structures/structures.types";

const requiredString = (t: TFunction, key: string) =>
  z.string().min(1, t(key));

const GEOMETRY_DECIMAL_INVALID =
  "admin.structures.geometry.validation.decimalInvalid";
const HYDROLOGY_DECIMAL_INVALID =
  "admin.structures.hydrology.validation.decimalInvalid";

const requiredGeometryDecimal = (t: TFunction, requiredKey: string) =>
  requiredApiDecimalString(t, requiredKey, GEOMETRY_DECIMAL_INVALID);

const requiredHydrologyDecimal = (t: TFunction, requiredKey: string) =>
  requiredApiDecimalString(t, requiredKey, HYDROLOGY_DECIMAL_INVALID);

export const createAdminStructureDamGeometrySchema = (t: TFunction) =>
  z.object({
    crestElevation: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.crestElevationRequired",
    ),
    damHeight: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.damHeightRequired",
    ),
    crestLength: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.crestLengthRequired",
    ),
    crestWidth: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.crestWidthRequired",
    ),
    typicalBermWidth: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.typicalBermWidthRequired",
    ),
    upstreamSlopeInclination: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.upstreamSlopeInclinationRequired",
    ),
    downstreamSlopeInclination: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.downstreamSlopeInclinationRequired",
    ),
    betweenBermsSlopeInclination: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.betweenBermsSlopeInclinationRequired",
    ),
    globalDownstreamSlopeInclination: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.globalDownstreamSlopeInclinationRequired",
    ),
    referenceDocument: requiredString(
      t,
      "admin.structures.geometry.validation.referenceDocumentRequired",
    ),
  });

export const createAdminStructureStockpileGeometrySchema = (t: TFunction) =>
  z.object({
    typicalBermWidth: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.typicalBermWidthRequired",
    ),
    benchSlopeInclination: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.benchSlopeInclinationRequired",
    ),
    globalSlopeInclination: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.globalSlopeInclinationRequired",
    ),
    referenceDocument: requiredString(
      t,
      "admin.structures.geometry.validation.referenceDocumentRequired",
    ),
  });

export const createAdminStructureOpenPitGeometrySchema = (t: TFunction) =>
  z.object({
    sectors: z
      .union([
        z.literal(""),
        z
          .number({
            invalid_type_error: t(
              "admin.structures.geometry.validation.sectorsRequired",
            ),
          })
          .int(t("admin.structures.geometry.validation.sectorsInvalid"))
          .min(0, t("admin.structures.geometry.validation.sectorsInvalid")),
      ])
      .refine((value) => value !== "", {
        message: t("admin.structures.geometry.validation.sectorsRequired"),
      }),
    pitHeight: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.pitHeightRequired",
    ),
    typicalBermWidth: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.typicalBermWidthRequired",
    ),
    benchHeight: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.benchHeightRequired",
    ),
    benchInclination: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.benchInclinationRequired",
    ),
    globalSlopeInclination: requiredGeometryDecimal(
      t,
      "admin.structures.geometry.validation.globalSlopeInclinationRequired",
    ),
    referenceDocument: requiredString(
      t,
      "admin.structures.geometry.validation.referenceDocumentRequired",
    ),
  });

export const createAdminStructureGeometrySchema = (
  t: TFunction,
  structureType: StructureType,
) => {
  if (structureType === "stockpile") {
    return createAdminStructureStockpileGeometrySchema(t);
  }
  if (structureType === "openPit") {
    return createAdminStructureOpenPitGeometrySchema(t);
  }
  return createAdminStructureDamGeometrySchema(t);
};

export const createAdminStructureDamHydrologySchema = (t: TFunction) =>
  z.object({
    maximumMaximorumLevel: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.maximumMaximorumLevelRequired",
    ),
    criticalDuration: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.criticalDurationRequired",
    ),
    inflowRate: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.inflowRateRequired",
    ),
    outflowRate: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.outflowRateRequired",
    ),
    designRainfall: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.designRainfallRequired",
    ),
    contributingBasinArea: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.contributingBasinAreaRequired",
    ),
    returnPeriod: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.returnPeriodRequired",
    ),
    totalVolumeAtCrestElevation: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.totalVolumeAtCrestElevationRequired",
    ),
    spillwayType: z.enum(
      ["surface", "tulip", "lateralChannel", "labyrinth", "gated", "bottom"],
      {
        message: t("admin.structures.hydrology.validation.spillwayTypeRequired"),
      },
    ),
    spillwaySill: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.spillwaySillRequired",
    ),
    referenceDocument: requiredString(
      t,
      "admin.structures.hydrology.validation.referenceDocumentRequired",
    ),
  });

export const createAdminStructureStockpileHydrologySchema = (t: TFunction) =>
  z.object({
    criticalDuration: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.criticalDurationRequired",
    ),
    outflowRate: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.outflowRateRequired",
    ),
    designRainfall: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.designRainfallRequired",
    ),
    returnPeriod: requiredHydrologyDecimal(
      t,
      "admin.structures.hydrology.validation.returnPeriodRequired",
    ),
    referenceDocument: requiredString(
      t,
      "admin.structures.hydrology.validation.referenceDocumentRequired",
    ),
  });

export const createAdminStructureHydrologySchema = (
  t: TFunction,
  structureType: StructureType,
) => {
  if (structureType === "stockpile") {
    return createAdminStructureStockpileHydrologySchema(t);
  }
  return createAdminStructureDamHydrologySchema(t);
};

export type AdminStructureDamGeometryForm = z.infer<
  ReturnType<typeof createAdminStructureDamGeometrySchema>
>;
export type AdminStructureStockpileGeometryForm = z.infer<
  ReturnType<typeof createAdminStructureStockpileGeometrySchema>
>;
export type AdminStructureOpenPitGeometryForm = z.input<
  ReturnType<typeof createAdminStructureOpenPitGeometrySchema>
>;
export type AdminStructureGeometryForm =
  | AdminStructureDamGeometryForm
  | AdminStructureStockpileGeometryForm
  | AdminStructureOpenPitGeometryForm;

export type AdminStructureDamHydrologyForm = z.infer<
  ReturnType<typeof createAdminStructureDamHydrologySchema>
>;
export type AdminStructureStockpileHydrologyForm = z.infer<
  ReturnType<typeof createAdminStructureStockpileHydrologySchema>
>;
export type AdminStructureHydrologyForm =
  | AdminStructureDamHydrologyForm
  | AdminStructureStockpileHydrologyForm;
